import { Box, styled, TextField, Autocomplete } from "@mui/material";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import AuditInfoLabel from "../../../components/AuditInfoLabel";
import { countries } from "../../../global";

const StyledBox = styled(Box)(() => ({
  display: 'flex',
  marginBottom: "20px"
}));

const ExternalLaboratoryShippingForm = ({ 
  shippingAddressData, 
  formikShippingRef, 
  canEdit=false}) => {

  const initialValues = {
    recipientName: shippingAddressData?.recipientName ?? '',
    company: shippingAddressData?.company ?? '',
    department: shippingAddressData?.department ?? '',
    address1: shippingAddressData?.address1 ?? '',
    city: shippingAddressData?.city ?? '',
    state: shippingAddressData?.state ?? '',
    country: shippingAddressData?.country ?? '',
    postalCode: shippingAddressData?.postalCode ?? '',
    phoneNumber1: shippingAddressData?.phoneNumber1 ?? '',
    emailAddress: shippingAddressData?.emailAddress ?? '',
    createdByEmail: shippingAddressData?.createdByEmail,
    createdDate: shippingAddressData?.createdDate,
    lastModifiedByEmail: shippingAddressData?.lastModifiedByEmail,
    lastModifiedDate: shippingAddressData?.lastModifiedDate
  };

  const shippingAddressValidationSchema = Yup.object({
    recipientName: Yup.string()
      .transform((value) => (value ? value.trim() : ''))
      .required('Recipient Name is required'),
    company: Yup.string()
      .transform((value) => (value ? value.trim() : ''))
      .required('Company is required'),
    address1: Yup.string()
      .transform((value) => (value ? value.trim() : ''))
      .required('Street Address is required'),
    city: Yup.string()
      .transform((value) => (value ? value.trim() : ''))
      .required('City is required'),
    country: Yup.string()
      .transform((value) => (value ? value.trim() : ''))
      .required('Country is required'),
      emailAddress: Yup.string()
        .email('Invalid email format')
        .max(60, 'Email must be 60 characters or less')
  });

  return (
    <Formik
      initialValues={initialValues}
      innerRef={formikShippingRef}
      enableReinitialize={true}
      validationSchema={shippingAddressValidationSchema}
    >
      {({ values, handleChange, handleBlur, setFieldValue, setFieldTouched, errors, touched, isValid, dirty }) => (
        <Form style={{margin:"10px", width: "60rem"}}>

          <StyledBox>
            <TextField
              style={{width: "25rem"}}
              name="recipientName"
              label="* Recipient's Name"
              placeholder="Recipient's Name"
              variant="outlined" size="small" margin="none" 
              value={values.recipientName}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={!canEdit}
              InputLabelProps={{ shrink: true }}
              error={touched.recipientName && Boolean(errors.recipientName)}
              helperText={touched.recipientName && errors.recipientName}
            />

            <TextField
              style={{width: "20rem", marginLeft:"20px"}}
              name="emailAddress"
              label="Recipient's Email"
              placeholder="Recipient's Email"
              variant="outlined" size="small" margin="none" 
              value={values.emailAddress}
              onChange={handleChange}
              onBlur={handleBlur}
              inputProps={{ maxLength: 60 }}
              disabled={!canEdit}
              InputLabelProps={{ shrink: true }}
              error={touched.emailAddress && Boolean(errors.emailAddress)}
              helperText={touched.emailAddress && errors.emailAddress}
            />

            <TextField
              style={{width: "15rem", marginLeft:"20px"}}
              name="phoneNumber1"
              label="Phone #"
              placeholder="Phone #"
              variant="outlined" size="small" margin="none" 
              value={values.phoneNumber1}
              onChange={handleChange}
              onBlur={handleBlur}
              inputProps={{ maxLength: 30 }}
              disabled={!canEdit}
              InputLabelProps={{ shrink: true }}
              error={touched.phoneNumber1 && Boolean(errors.phoneNumber1)}
              helperText={touched.phoneNumber1 && errors.phoneNumber1}
            />          
          </StyledBox>

          <StyledBox>
            <TextField
              style={{width: "40rem"}}
              name="company"
              label="* Company"
              placeholder="Company"
              variant="outlined" size="small" margin="none" 
              value={values.company}
              onChange={handleChange}
              onBlur={handleBlur}
              inputProps={{ maxLength: 255 }}
              disabled={!canEdit}
              InputLabelProps={{ shrink: true }}
              error={touched.company && Boolean(errors.company)}
              helperText={touched.company && errors.company}
            />

            <TextField
              style={{width: "20rem", marginLeft:"20px"}}
              name="department"
              label="Department/Floor No."
              placeholder="Department/Floor No."
              variant="outlined" size="small" margin="none" 
              value={values.department}
              onChange={handleChange}
              onBlur={handleBlur}
              inputProps={{ maxLength: 255 }}
              disabled={!canEdit}
              InputLabelProps={{ shrink: true }}      
              error={touched.department && Boolean(errors.department)}
              helperText={touched.department && errors.department}
            />       
          </StyledBox>

          <StyledBox>
            <TextField
              style={{width: "60rem"}}
              name="address1"
              label="* Street Address"
              placeholder="Street Address"
              variant="outlined" size="small" margin="none" 
              value={values.address1}
              onChange={handleChange}
              onBlur={handleBlur}
              inputProps={{ maxLength: 255 }}
              disabled={!canEdit}
              InputLabelProps={{ shrink: true }}        
              error={touched.address1 && Boolean(errors.address1)}
              helperText={touched.address1 && errors.address1}
            />
          </StyledBox>

          <StyledBox>
            <TextField
              style={{width: "20rem"}}
              name="city"
              label="* City"
              placeholder="City"
              variant="outlined" size="small" margin="none" 
              value={values.city}
              onChange={handleChange}
              onBlur={handleBlur}
              inputProps={{ maxLength: 60 }}
              disabled={!canEdit}
              InputLabelProps={{ shrink: true }}
              error={touched.city && Boolean(errors.city)}
              helperText={touched.city && errors.city}
            />

            <TextField
              style={{width: "10rem", marginLeft:"20px"}}
              name="state"
              label="State"
              placeholder="State"
              variant="outlined" size="small" margin="none" 
              value={values.state}
              onChange={handleChange}
              onBlur={handleBlur}
              inputProps={{ maxLength: 20 }}
              disabled={!canEdit}
              InputLabelProps={{ shrink: true }}
              error={touched.state && Boolean(errors.state)}
              helperText={touched.state && errors.state}
            />

            <TextField
              style={{width: "10rem", marginLeft:"20px"}}
              name="postalCode"
              label="Zip/Postal Code"
              placeholder="Zip/Postal Code"
              variant="outlined" size="small" margin="none" 
              value={values.postalCode}
              onChange={handleChange}
              onBlur={handleBlur}
              inputProps={{ maxLength: 20 }}
              disabled={!canEdit}
              InputLabelProps={{ shrink: true }}
              error={touched.postalCode && Boolean(errors.postalCode)}
              helperText={touched.postalCode && errors.postalCode}
            />

            <Autocomplete
              noOptionsText={"No Country Found"}
              value={values.country}
              onChange={(e, val) => setFieldValue('country', val)}
              onBlur={() => setFieldTouched('country', true)}
              disablePortal
              disabled={!canEdit}
              options={countries.map(c => c)}
              sx={{ width: "18rem", marginLeft:"20px" }}
              renderInput={(params) => <TextField 
                {...params} 
                name="country"  variant="outlined" size="small" margin="none" 
                label="* Country"
                placeholder="Country"
                InputLabelProps={{ shrink: true }}
                error={touched.country && Boolean(errors.country)}
                helperText={touched.country && errors.country}
              />}
              autoSelect                  
            />          
          </StyledBox>

          <AuditInfoLabel auditObject={values} />
    
        </Form>
      )}
    </Formik>
  );
};

export default ExternalLaboratoryShippingForm;
